import * as angular from 'angular';
import * as _ from 'lodash';

import { CategoryConfiguration, Category } from '../../models/';
import { IRequestService } from '../request.service';
import { ILoggerService, ISessionStorageService } from '../general';

export interface ICategoryService {
    getAll(): angular.IPromise<any>;
    getAll(force: boolean): angular.IPromise<Category[]>;
    getAll(force?: boolean): angular.IPromise<Category[]>;
    getAllUnfiltered(force?: boolean): angular.IPromise<Category[]>;
    getCategory(guid: string): angular.IPromise<Category>;
    addCategory(category: Category): angular.IPromise<Category[]>;
    addCategories(categories: Category[]): angular.IPromise<Category[]>;
    duplicateCategory(guid: string): angular.IPromise<Category[]>;
    updateCategory(category: Category): angular.IPromise<Category[]>;
    updateCategories(categories: Category[]): angular.IPromise<Category[]>;
    deleteCategory(guid: string): angular.IPromise<Category[]>;
    deleteCategories(guids: string[]): angular.IPromise<Category[]>;
}

export class CategoryService implements ICategoryService {
    static serviceId = 'categoryService';
    static $inject = [
        '$q',
        'requestService',
        'sessionStorageService',
        'loggerService'];

    private static _serviceUrl = 'category';

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _storageService: ISessionStorageService,
        private _loggerService: ILoggerService) {
    }

    /**
     * Get a category based on a guid provided
     * @param guid guid of the category to find
     */
    getCategory(guid: string): angular.IPromise<Category> {
        let promise: angular.IPromise<Category>;
        try {
            if (guid) {
                const stored = this._storageService.getTypedFrom<Category>(guid);
                if (!stored) {
                    promise = this._requestService.getAtV1<Category>(`${CategoryService._serviceUrl}/${guid}`, undefined, true);
                } else {
                    promise = this._$q.when(stored);
                }
            } else {
                promise = this._$q.when(new Category());
            }
        } catch (e) {
            this._loggerService.logError(e);
            promise = this._$q.reject(e);
        }
        return promise;
    }

    /**
     * Get all categories
     */
    getAll(): angular.IPromise<Category[]>;
    getAll(force: boolean): angular.IPromise<Category[]>;
    getAll(force?: boolean): angular.IPromise<Category[]> {
        const controllerAction: string = `${CategoryService._serviceUrl}`;
        if (force) {
            this._requestService.cleanCacheAtV1(controllerAction);
        }
        return this._requestService.getAtV1(controllerAction, undefined, true)
            .then((data: Category[]) => {
                data.forEach(d => {
                    this._storageService.setTypedTo(d.guid, d);
                });
                return this.getCategories(data);
            });
    }
    getAllUnfiltered(): angular.IPromise<Category[]>;
    getAllUnfiltered(force: boolean): angular.IPromise<Category[]>;
    getAllUnfiltered(force?: boolean): angular.IPromise<Category[]> {
        const controllerAction: string = `${CategoryService._serviceUrl}/filter`;
        if (force) {
            this._requestService.cleanCacheAtV1(controllerAction, { filter: false });
        }
        return this._requestService.getAtV1(controllerAction, { filter: false }, true);
    }

    /**
     * Add a new category
     * @param category Category to add
     * @param applicationName optional parameter, 
     * specify for which application the category should be added
     * (By default, current application)
     */
    addCategory(category: Category, applicationName?: string): angular.IPromise<Category[]> {
        let controllerAction: string = `${CategoryService._serviceUrl}`;
        if (applicationName) {
            controllerAction = controllerAction.concat(`${applicationName}`);
        }
        return this._requestService.postAtV1(controllerAction, category, undefined, true)
            .then((data: Category[]) => {
                this._requestService.cleanCache(controllerAction);
                this.cleanCategoryData(category.guid);
                const newCategory = _.find(data, d => d.guid === category.guid);
                if (newCategory) {
                    this._storageService.setTypedTo(newCategory.guid, newCategory);
                }
                return this.getCategories(data);
            });
    }

    /**
     * Add a array of new categories
     * @param categories Array of the new categories to add
     */
    addCategories(categories: Category[]): angular.IPromise<Category[]> {
        let promise: angular.IPromise<Category[]>;
        const controllerAction: string = `${CategoryService._serviceUrl}/addAll`;

        const toSave = [];
        try {
            categories.forEach(d => {
                const category = new Category();
                category.configuration = <CategoryConfiguration>d.configuration;
                category.guid = d.guid;
                category.name = d.name;
                category.sortOrder = d.sortOrder;
                toSave.push(category);
            });
        } catch (e) {
            this._loggerService.logError('Error while preparing Categories.');
        }

        if (toSave.length > 0) {
            promise = this._requestService.postAtV1(controllerAction, categories, undefined, true)
                .then((data: Category[]) => {
                    this._requestService.cleanCacheAtV1(controllerAction);
                    categories.forEach((element) => {
                        this.cleanCategoryData(element.guid);
                        const newCategory = _.find(data, d => d.guid === element.guid);
                        if (newCategory) {
                            this._storageService.setTypedTo(newCategory.guid, newCategory);
                        }
                    });
                    return this.getCategories(data);
                });
        } else {
            promise = this._$q.when([]);
        }
        return promise;
    }

    /**
     * Duplicate a category
     * @param guid guid of the original category
     */
    duplicateCategory(guid: string): angular.IPromise<Category[]> {
        const controllerAction: string = `${CategoryService._serviceUrl}/duplicate/${guid}`;

        return this._requestService.postAtV1(controllerAction, undefined, undefined, true)
            .then((data: Category[]) => {
                this._requestService.cleanCacheAtV1(controllerAction);
                return this.getCategories(data);
            });
    }

    /**
     * Update a category
     * @param category Category to be updated
     */
    updateCategory(category: Category): angular.IPromise<Category[]> {
        const controllerAction: string = `${CategoryService._serviceUrl}/update`;

        return this._requestService.postAtV1(controllerAction, category)
            .then((data: Category[]) => {
                data.forEach(d => {
                    this._storageService.setTypedTo(d.guid, d);
                });
                return this.getCategories(data);
            });
    }

    /**
     * Update all provided categories
     * @param categories Categories to be updated
     */
    updateCategories(categories: Category[]): angular.IPromise<Category[]> {
        let promise: angular.IPromise<Category[]>;
        const controllerAction: string = `${CategoryService._serviceUrl}/updateAll`;

        const toSave = [];
        try {
            categories.forEach(d => {
                const category = new Category();
                category.configuration = <CategoryConfiguration>d.configuration;
                category.guid = d.guid;
                category.name = d.name;
                category.sortOrder = d.sortOrder;
                toSave.push(category);
            });
        } catch (e) {
            this._loggerService.logError('Error while preparing Categories.');
        }

        if (toSave.length > 0) {
            promise = this._requestService.postAtV1(controllerAction, categories, undefined, true)
                .then((data: Category[]) => {
                    data.forEach((element) => {
                        this._requestService.cleanCacheAtV1(`${CategoryService._serviceUrl}/${element.guid}`);
                    });
                    return this.getCategories(data);
                });
        }
        return promise;
    }

    /**
     * Delete a category
     * @param guid guid of the category to delete
     */
    deleteCategory(guid: string): angular.IPromise<Category[]> {
        
        const controllerAction: string = `${CategoryService._serviceUrl}/${guid}`;

        return this._requestService.deleteAtV1(controllerAction)
            .then(data => {
                this._requestService.cleanCacheAtV1(controllerAction);
                return this.getCategories(data);
            });
    }

    /**
     * Delete multiple categories
     * @param guid guid of the categories to delete
     */
    deleteCategories(guids: string[]): angular.IPromise<Category[]> {
        
        const controllerAction: string = `${CategoryService._serviceUrl}/deleteAll`;

        return this._requestService.postAtV1(controllerAction, guids)
            .then((data: Category[]) => {
                this._requestService.cleanCache(controllerAction);
                return this.getCategories(data);
            });
    }

    /**
     * Sort categories list
     */
    private getCategories(data: Category[]): Category[] {
        let category = [];
        data.forEach((d, i) => {
            category.push(d);
        });
        category = _.sortBy(category, d => d.name);
        return category;
    }

    /**
     * Empty cache for a specific guid
     * @param guid guid du cache à vider
     */
    private cleanCategoryData(guid: string): void {
        this._requestService.cleanCache(CategoryService._serviceUrl + `/${guid}`);
        this._requestService.cleanCache(CategoryService._serviceUrl);
        this._storageService.remove(guid);
    }
}